import React from 'react';
import axios from 'axios';
import { Button, Select, Switch, Spin, message, Alert, Table } from 'antd';
import { LoadingOutlined, PlusOutlined, SaveOutlined, SyncOutlined } from '@ant-design/icons';
import * as serverconfig from '../serverconn';
import { companyAccountOptionLabel, filterCompanyAccountOption } from '../journalReportUtils';

var CryptoJS = require("crypto-js");

const { Option } = Select;
const antIcon = <LoadingOutlined style={{ fontSize: 60 }} spin />;

var token = '';
var sacco = '';

/** Multiple ledger rows allowed for loan types */
const MULTI_LEDGER_SUBACCOUNTS = ['loan', 'loan_interest'];

/** Pitech Transaction.subaccount keys (same as existing multitenant clients). */
const SUBACCOUNT_OPTIONS = [
  { key: 'savings', label: 'Ordinary savings' },
  { key: 'savings_interest', label: 'Savings interest' },
  { key: 'share_number', label: 'Share number' },
  { key: 'share_amount', label: 'Share amount' },
  { key: 'loan', label: 'Loan balance', multiLedger: true },
  { key: 'loan_interest', label: 'Loan interest', multiLedger: true },
  { key: 'loan_fine', label: 'Loan fine' },
  { key: 'fixed_deposit', label: 'Fixed deposit' },
  { key: 'savings_loansecurity', label: 'Savings loan security' },
  { key: 'shares_loansecurity', label: 'Shares loan security' },
  { key: 'accumulated_dividends', label: 'Accumulated dividends' },
];

let nextRowUid = 1;
const makeRowUid = () => `row-${nextRowUid++}`;

const normalizeSubaccountKey = (key) => {
  if (key === 'loan_account') return 'loan';
  if (key === 'def_deposit') return 'fixed_deposit';
  return key;
};

const parseSettingsList = (data) => {
  if (Array.isArray(data)) {
    return data;
  }
  if (data && Array.isArray(data.results)) {
    return data.results;
  }
  return [];
};

class MemberSubaccountLedgerSettings extends React.Component {
  state = {
    datarequested: true,
    companyaccounts: [],
    rows: [],
    saving: false,
  };

  componentDidMount() {
    this.initSession();
    this.loadData();
  }

  initSession = () => {
    if (localStorage.getItem("sacco")) {
      sacco = CryptoJS.AES.decrypt(localStorage.getItem("sacco"), 'my-secret-key@12345').toString(CryptoJS.enc.Utf8);
    } else {
      sacco = '';
    }
    if (localStorage.getItem("token")) {
      token = localStorage.getItem("token");
    } else {
      token = '';
    }
    axios.defaults.headers = {
      "Content-Type": "application/json",
      Authorization: `Token ${token}`,
    };
  };

  buildRowsFromSettings = (settings) => {
    const byKey = {};
    parseSettingsList(settings).forEach((s) => {
      const key = normalizeSubaccountKey(s.subaccount_key);
      if (!byKey[key]) {
        byKey[key] = [];
      }
      byKey[key].push({ ...s, subaccount_key: key });
    });

    const rows = [];
    SUBACCOUNT_OPTIONS.forEach((opt, index) => {
      const existingList = byKey[opt.key] || [];
      const isMulti = MULTI_LEDGER_SUBACCOUNTS.includes(opt.key);

      if (isMulti) {
        if (existingList.length === 0) {
          rows.push({
            row_uid: makeRowUid(),
            key: `${opt.key}-0`,
            subaccount_key: opt.key,
            subaccount_label: opt.label,
            multi_ledger: true,
            setting_id: null,
            company_account: null,
            is_active: false,
            display_order: index,
          });
        } else {
          existingList.forEach((existing, i) => {
            rows.push({
              row_uid: makeRowUid(),
              key: `${opt.key}-${existing.id}`,
              subaccount_key: opt.key,
              subaccount_label: i === 0 ? opt.label : `${opt.label} (${i + 1})`,
              multi_ledger: true,
              setting_id: existing.id,
              company_account: existing.company_account,
              is_active: existing.is_active,
              display_order: existing.display_order,
            });
          });
        }
        return;
      }

      const existing = existingList[0];
      rows.push({
        row_uid: makeRowUid(),
        key: opt.key,
        subaccount_key: opt.key,
        subaccount_label: opt.label,
        multi_ledger: false,
        setting_id: existing ? existing.id : null,
        company_account: existing ? existing.company_account : null,
        is_active: existing ? existing.is_active : false,
        display_order: existing ? existing.display_order : index,
      });
    });
    return rows;
  };

  loadData = () => {
    this.setState({ datarequested: true });
    return Promise.all([
      axios.get(`${serverconfig.backendserverurl}/api/member_subaccount_ledger_settings/?sacco=${sacco}`),
      axios.get(`${serverconfig.backendserverurl}/api/companyaccounts/?sacco=${sacco}`),
    ])
      .then(([settingsRes, accountsRes]) => {
        this.setState({
          rows: this.buildRowsFromSettings(settingsRes.data),
          companyaccounts: accountsRes.data,
          datarequested: false,
        });
      })
      .catch((err) => {
        console.log(err);
        message.error("Failed to load ledger mapping settings");
        this.setState({ datarequested: false });
      });
  };

  updateRow = (rowUid, field, value) => {
    const rows = this.state.rows.map((row) => {
      if (row.row_uid !== rowUid) {
        return row;
      }
      const next = { ...row, [field]: value };
      if (field === 'company_account') {
        next.is_active = Boolean(value);
      }
      return next;
    });
    this.setState({ rows });
  };

  addMultiLedgerRow = (subaccountKey) => {
    const opt = SUBACCOUNT_OPTIONS.find((o) => o.key === subaccountKey);
    if (!opt) {
      return;
    }
    const sameKeyRows = this.state.rows.filter((r) => r.subaccount_key === subaccountKey);
    const rows = [
      ...this.state.rows,
      {
        row_uid: makeRowUid(),
        key: `${subaccountKey}-new-${Date.now()}`,
        subaccount_key: subaccountKey,
        subaccount_label: `${opt.label} (${sameKeyRows.length + 1})`,
        multi_ledger: true,
        setting_id: null,
        company_account: null,
        is_active: true,
        display_order: sameKeyRows.length,
      },
    ];
    this.setState({ rows });
  };

  seedFromSacco = () => {
    this.setState({ saving: true });
    const form_data = new FormData();
    form_data.append('sacco', sacco);
    axios.post(`${serverconfig.backendserverurl}/customqueries/seed_member_subaccount_ledger_settings`, form_data, {
      headers: { 'content-type': 'multipart/form-data' },
    })
      .then((res) => {
        message.success(`Seeded ${res.data.settings_created_or_updated} mapping(s) from Sacco profile`);
        return this.loadData();
      })
      .catch((err) => {
        console.log(err);
        message.error("Seed failed");
      })
      .finally(() => {
        this.setState({ saving: false });
      });
  };

  resolveSettingId = (row, existingSettings) => {
    if (row.setting_id) {
      return row.setting_id;
    }
    if (!row.company_account) {
      return null;
    }
    const subaccountKey = normalizeSubaccountKey(row.subaccount_key);
    const companyAccountId = Number(row.company_account);
    const match = existingSettings.find(
      (s) => normalizeSubaccountKey(s.subaccount_key) === subaccountKey
        && Number(s.company_account) === companyAccountId
    );
    return match ? match.id : null;
  };

  saveAll = async () => {
    this.initSession();
    const saccoId = Number(sacco);
    if (!saccoId) {
      message.error('Sacco session missing. Refresh the page and try again.');
      return;
    }

    this.setState({ saving: true });
    let created = 0;
    let updated = 0;
    let removed = 0;
    try {
      const settingsRes = await axios.get(
        `${serverconfig.backendserverurl}/api/member_subaccount_ledger_settings/?sacco=${saccoId}`
      );
      const existingSettings = parseSettingsList(settingsRes.data);
      const seenPersistKeys = new Set();

      for (const row of this.state.rows) {
        const shouldPersist = Boolean(row.company_account && row.is_active);
        const settingId = this.resolveSettingId(row, existingSettings);

        if (settingId) {
          if (!shouldPersist) {
            await axios.delete(
              `${serverconfig.backendserverurl}/api/member_subaccount_ledger_settings/${settingId}/`
            );
            removed += 1;
            continue;
          }
          const payload = {
            sacco: saccoId,
            subaccount_key: row.subaccount_key,
            company_account: row.company_account,
            is_active: true,
            display_order: row.display_order,
          };
          await axios.put(
            `${serverconfig.backendserverurl}/api/member_subaccount_ledger_settings/${settingId}/`,
            payload
          );
          updated += 1;
          continue;
        }

        if (shouldPersist) {
          const dedupeKey = `${row.subaccount_key}:${row.company_account}`;
          if (seenPersistKeys.has(dedupeKey)) {
            continue;
          }
          seenPersistKeys.add(dedupeKey);

          const payload = {
            sacco: saccoId,
            subaccount_key: row.subaccount_key,
            company_account: row.company_account,
            is_active: true,
            display_order: row.display_order,
          };
          await axios.post(
            `${serverconfig.backendserverurl}/api/member_subaccount_ledger_settings/`,
            payload
          );
          created += 1;
        }
      }
      const parts = [];
      if (created) parts.push(`${created} added`);
      if (updated) parts.push(`${updated} updated`);
      if (removed) parts.push(`${removed} removed`);
      message.success(
        parts.length ? `Mappings saved (${parts.join(', ')})` : 'No active mappings to save — pick a ledger account (Active turns on automatically)'
      );
      await this.loadData();
    } catch (err) {
      console.log(err);
      const detail = err.response?.data;
      message.error(
        detail && typeof detail === 'object'
          ? `Save failed: ${JSON.stringify(detail)}`
          : 'Save failed (duplicate ledger account for same subaccount is not allowed)'
      );
    }
    this.setState({ saving: false });
  };

  render() {
    if (this.state.datarequested) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 40 }}>
          <Spin indicator={antIcon} />
        </div>
      );
    }

    const activeMapped = this.state.rows.filter((r) => r.is_active && r.company_account).length;
    const loanRows = this.state.rows.filter((r) => r.subaccount_key === 'loan').length;
    const loanIntRows = this.state.rows.filter((r) => r.subaccount_key === 'loan_interest').length;

    const columns = [
      {
        title: 'Member subaccount',
        dataIndex: 'subaccount_label',
        key: 'subaccount_label',
        render: (text, record) => (
          <span>
            {text}
            {record.multi_ledger && (
              <Button
                type="link"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => this.addMultiLedgerRow(record.subaccount_key)}
                style={{ paddingLeft: 8 }}
              >
                Add ledger
              </Button>
            )}
          </span>
        ),
      },
      {
        title: 'Ledger account (companyAccounts)',
        key: 'company_account',
        render: (_, record) => (
          <Select
            showSearch
            allowClear
            style={{ width: '100%', minWidth: 280 }}
            placeholder="Select chart of account (search by name or code)"
            value={record.company_account || undefined}
            filterOption={filterCompanyAccountOption}
            onChange={(val) => this.updateRow(record.row_uid, 'company_account', val)}
          >
            {this.state.companyaccounts.map((acc) => (
              <Option key={acc.id} value={acc.id} companyAccount={acc}>
                {companyAccountOptionLabel(acc)}
              </Option>
            ))}
          </Select>
        ),
      },
      {
        title: 'Active',
        key: 'is_active',
        width: 90,
        render: (_, record) => (
          <Switch
            checked={record.is_active}
            onChange={(val) => this.updateRow(record.row_uid, 'is_active', val)}
          />
        ),
      },
    ];

    return (
      <div>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Member balances report (GeneralJournal)"
          description="Subaccount keys match existing Pitech Transaction.subaccount values (loan, fixed_deposit, …). Report API fields remain savings_balance, loan_balance, fixed_deposit_balance, etc."
        />

        <Alert
          type={activeMapped >= 5 ? 'success' : 'warning'}
          showIcon
          style={{ marginBottom: 16 }}
          message={`${activeMapped} active ledger mapping(s)`}
          description={activeMapped === 0
            ? "Pick a ledger account for each subaccount (Active turns on automatically), then Save."
            : `Loan mappings: ${loanRows} ledger row(s) for principal, ${loanIntRows} for interest.`}
        />

        <div style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<SyncOutlined />}
            onClick={this.seedFromSacco}
            loading={this.state.saving}
            style={{ marginRight: 8 }}
          >
            Seed from Sacco profile
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={this.saveAll}
            loading={this.state.saving}
            style={{ marginRight: 8 }}
          >
            Save all mappings
          </Button>
          <Button onClick={this.loadData}>Refresh</Button>
        </div>

        <Table
          columns={columns}
          dataSource={this.state.rows}
          pagination={false}
          size="small"
          bordered
          rowKey="row_uid"
        />
      </div>
    );
  }
}

export default MemberSubaccountLedgerSettings;
